import { Heading, Section, Stack } from "@carbon/react";
import { FC } from "react";

import { TextAreaFormField } from "src/components/Form/TextAreaFormField";
import { TextInputFormField } from "src/components/Form/TextInputFormField";
import { CaseType } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { useT } from "src/internationalization";
import { ClientSchemaIdentifier } from "src/types/clientSchema";

import { SecondaryCriteria, SecondaryCriteriaTable } from "../linkedCase/SecondaryCriteria";

import { DetailsFormControl } from "./useDetailsForm";

export type DetailsFormProps = {
  detailsFormControl: DetailsFormControl;
  identifier?: ClientSchemaIdentifier;
  secondaryCriteriaList?: SecondaryCriteria[];
  selectedCaseTypes: CaseType[];
};

export const DetailsForm: FC<DetailsFormProps> = ({
  detailsFormControl,
  identifier,
  secondaryCriteriaList,
  selectedCaseTypes,
}) => {
  const t = useT();

  const selectedCaseTypeNames = selectedCaseTypes.map((caseType) => caseType.name);

  return (
    <Stack gap={6} className="mb-4">
      <Section level={3}>
        <Heading className={"ml-0"}>
          {t("page.rules.modal.create.linkedCase.form.details.title")}
        </Heading>
      </Section>
      {identifier && (
        <div className="flex">
          <div className="mr-2">
            {t("page.rules.modal.create.linkedCase.form.details.summary.condition")}
          </div>
          <div className="mr-2">
            {t("page.rules.modal.create.linkedCase.form.details.summary.identifier")}
          </div>
          <div className="text-backgroundBrand">{identifier.identifierName}</div>
          <div className="ml-2 mr-2">
            {t("page.rules.modal.create.linkedCase.form.details.summary.case")}
          </div>
          <div className="text-backgroundBrand">{identifier.elementName}</div>
        </div>
      )}
      {selectedCaseTypeNames.length > 0 && (
        <div className="flex">
          <div className="mr-2">{t("page.rules.modal.create.linkedCase.form.caseType.title")}:</div>
          <div className="text-backgroundBrand">{selectedCaseTypeNames.join(", ")}</div>
        </div>
      )}
      {secondaryCriteriaList && secondaryCriteriaList.length > 0 && (
        <SecondaryCriteriaTable data={secondaryCriteriaList} />
      )}
      <div className="flex gap-2">
        <TextInputFormField
          id="name"
          name="name"
          label={t("general.form.name.label")}
          helperText={t("page.rules.modal.create.linkedCase.form.name.helper")}
          control={detailsFormControl.control}
        />
        <TextInputFormField
          id="code"
          name="code"
          label={t("general.form.code.label")}
          helperText={t("page.rules.modal.create.linkedCase.form.code.helper")}
          control={detailsFormControl.control}
        />
      </div>
      <TextAreaFormField
        id="description"
        name="description"
        label={t("general.form.description.label")}
        helperText={t("page.rules.modal.create.linkedCase.form.description.helper")}
        control={detailsFormControl.control}
      />
    </Stack>
  );
};
